/**
 * OAuth Service for External Calendar Integration.
 *
 * Handles PKCE-based OAuth 2.0 authorization flows for connecting Google Calendar,
 * Microsoft Outlook/365, and Apple Calendar. Communicates with the backend
 * OAuth handler via REST API for token exchange, refresh, and revocation.
 *
 * This service is NOT offline-first — OAuth flows require network connectivity.
 *
 * @module oauthService
 * @see Requirements 6.1, 6.2, 6.7, 6.8, 17.1
 */

import type { CalendarProvider, ExternalCalendarConnection } from '../types/calendar';
import { getIdToken } from './auth';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Maximum number of external calendar connections allowed per user. */
const MAX_CONNECTIONS = 5;

/** PKCE code_verifier minimum length (per RFC 7636). */
const CODE_VERIFIER_MIN_LENGTH = 43;

/** PKCE code_verifier maximum length (per RFC 7636). */
const CODE_VERIFIER_MAX_LENGTH = 128;

/** Characters allowed in the code_verifier (unreserved characters per RFC 7636). */
const VERIFIER_CHARSET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

/** Storage key for PKCE code_verifier during OAuth redirect. */
const PKCE_VERIFIER_KEY = 'kira_oauth_code_verifier';

/** Storage key for OAuth state parameter during redirect. */
const OAUTH_STATE_KEY = 'kira_oauth_state';

/** Storage key for the provider being connected during OAuth redirect. */
const OAUTH_PROVIDER_KEY = 'kira_oauth_provider';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Result of initiating an OAuth flow. */
export interface OAuthInitResult {
  /** The provider authorization URL to redirect the user to. */
  authUrl: string;
  /** The state parameter for CSRF protection. */
  state: string;
}

/** Result of handling the OAuth callback. */
export interface OAuthCallbackResult {
  /** The new connection ID for the connected calendar. */
  connectionId: string;
  /** The provider that was connected. */
  provider: CalendarProvider;
  /** The email associated with the provider account. */
  providerEmail: string;
}

/** Result of a token refresh operation. */
export interface TokenRefreshResult {
  /** The new access token. */
  accessToken: string;
  /** ISO 8601 timestamp when the new access token expires. */
  expiresAt: string;
}

/** Options for creating the OAuthService (useful for testing). */
export interface OAuthServiceOptions {
  /** Base URL for the Kira API. Defaults to VITE_API_URL env var or '/api'. */
  apiBaseUrl?: string;
  /** Custom fetch implementation (for testing). */
  fetchFn?: typeof fetch;
}

// ─── OAuth Service Interface ─────────────────────────────────────────────────

export interface OAuthService {
  /** Initiate an OAuth flow for the given provider. Returns the auth URL and state. */
  initiateOAuth(provider: CalendarProvider): Promise<OAuthInitResult>;
  /** Handle the OAuth callback after user authorization. */
  handleCallback(
    code: string,
    state: string,
    provider: CalendarProvider
  ): Promise<OAuthCallbackResult>;
  /** Refresh the access token for a connection. */
  refreshToken(connectionId: string): Promise<TokenRefreshResult>;
  /** Revoke tokens and disconnect a calendar connection. */
  revokeConnection(connectionId: string): Promise<void>;
  /** Get all external calendar connections for the current user. */
  getConnections(): Promise<ExternalCalendarConnection[]>;
}

// ─── PKCE Utility Functions ──────────────────────────────────────────────────

/**
 * Generate a cryptographically random PKCE code_verifier.
 * Length is between 43 and 128 characters (uses 128 for maximum entropy).
 */
export function generateCodeVerifier(length: number = CODE_VERIFIER_MAX_LENGTH): string {
  const clampedLength = Math.max(
    CODE_VERIFIER_MIN_LENGTH,
    Math.min(length, CODE_VERIFIER_MAX_LENGTH)
  );

  const randomValues = new Uint8Array(clampedLength);
  crypto.getRandomValues(randomValues);

  let verifier = '';
  for (let i = 0; i < clampedLength; i++) {
    verifier += VERIFIER_CHARSET[randomValues[i]! % VERIFIER_CHARSET.length];
  }

  return verifier;
}

/**
 * Generate a PKCE code_challenge from a code_verifier using SHA-256.
 * Returns the base64url-encoded hash (no padding).
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);

  // Convert ArrayBuffer to base64url (no padding)
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Generate a random state parameter for CSRF protection.
 */
export function generateState(): string {
  const randomValues = new Uint8Array(32);
  crypto.getRandomValues(randomValues);
  return Array.from(randomValues, (b) => b.toString(16).padStart(2, '0')).join('');
}

// ─── PKCE Storage Helpers ────────────────────────────────────────────────────

/**
 * Store the PKCE code_verifier in sessionStorage for the duration of the OAuth redirect.
 * Uses sessionStorage to limit lifetime to the browser session.
 */
export function storeCodeVerifier(verifier: string): void {
  try {
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  } catch {
    // sessionStorage may not be available; fallback handled by caller
  }
}

/**
 * Retrieve and remove the stored PKCE code_verifier.
 */
export function retrieveCodeVerifier(): string | null {
  try {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    return verifier;
  } catch {
    return null;
  }
}

/**
 * Store the OAuth state parameter for validation on callback.
 */
export function storeOAuthState(state: string): void {
  try {
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
  } catch {
    // Best effort
  }
}

/**
 * Retrieve and remove the stored OAuth state parameter.
 */
export function retrieveOAuthState(): string | null {
  try {
    const state = sessionStorage.getItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    return state;
  } catch {
    return null;
  }
}

/**
 * Store the provider being connected during the OAuth redirect.
 */
export function storeOAuthProvider(provider: string): void {
  try {
    sessionStorage.setItem(OAUTH_PROVIDER_KEY, provider);
  } catch {
    // Best effort
  }
}

/**
 * Retrieve and remove the stored OAuth provider.
 */
export function retrieveOAuthProvider(): string | null {
  try {
    const provider = sessionStorage.getItem(OAUTH_PROVIDER_KEY);
    sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
    return provider;
  } catch {
    return null;
  }
}

// ─── OAuth Service Implementation ───────────────────────────────────────────

/**
 * Creates an OAuthService instance that communicates with the backend
 * calendar OAuth endpoints.
 */
export function createOAuthService(options: OAuthServiceOptions = {}): OAuthService {
  const apiBaseUrl = options.apiBaseUrl ?? import.meta.env.VITE_API_URL ?? '/api';
  const fetchFn = options.fetchFn ?? fetch;

  /**
   * Make an authenticated API request to the backend.
   */
  async function apiRequest<T>(
    path: string,
    method: string = 'GET',
    body?: Record<string, unknown>
  ): Promise<T> {
    const token = await getIdToken();
    if (!token) {
      throw new Error('Not authenticated. Please log in to manage calendar connections.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    const response = await fetchFn(`${apiBaseUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`
      );
    }

    // DELETE responses may not have a body
    if (response.status === 204 || method === 'DELETE') {
      return undefined as unknown as T;
    }

    return response.json() as Promise<T>;
  }

  // ─── Interface Implementation ──────────────────────────────────────────

  async function initiateOAuth(provider: CalendarProvider): Promise<OAuthInitResult> {
    // Enforce max connections limit client-side
    const connections = await getConnections();
    if (connections.length >= MAX_CONNECTIONS) {
      throw new Error(
        `Maximum of ${MAX_CONNECTIONS} external calendar connections reached. ` +
          'Disconnect an existing calendar before adding a new one.'
      );
    }

    // Generate PKCE parameters
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();

    // Store code_verifier and state locally for the callback
    storeCodeVerifier(codeVerifier);
    storeOAuthState(state);
    storeOAuthProvider(provider);

    // Request the authorization URL from the backend
    const result = await apiRequest<{ authUrl: string }>('/calendar/oauth/initiate', 'POST', {
      provider,
      codeChallenge,
      codeChallengeMethod: 'S256',
      state,
    });

    return {
      authUrl: result.authUrl,
      state,
    };
  }

  async function handleCallback(
    code: string,
    state: string,
    provider: CalendarProvider
  ): Promise<OAuthCallbackResult> {
    // Validate state parameter to prevent CSRF attacks
    const storedState = retrieveOAuthState();
    if (!storedState || storedState !== state) {
      throw new Error('OAuth state mismatch. This may indicate a CSRF attack. Please try again.');
    }

    // Retrieve the code_verifier stored before redirect
    const codeVerifier = retrieveCodeVerifier();
    if (!codeVerifier) {
      throw new Error(
        'PKCE code_verifier not found. The authorization session may have expired. Please try again.'
      );
    }

    // Exchange the authorization code for tokens via the backend
    const result = await apiRequest<OAuthCallbackResult>('/calendar/oauth/callback', 'POST', {
      code,
      codeVerifier,
      state,
      provider,
    });

    return result;
  }

  async function refreshToken(connectionId: string): Promise<TokenRefreshResult> {
    const result = await apiRequest<TokenRefreshResult>(
      `/calendar/oauth/refresh`,
      'POST',
      { connectionId }
    );
    return result;
  }

  async function revokeConnection(connectionId: string): Promise<void> {
    await apiRequest<void>(`/calendar/connections/${connectionId}`, 'DELETE');
  }

  async function getConnections(): Promise<ExternalCalendarConnection[]> {
    const result = await apiRequest<{ connections: ExternalCalendarConnection[] }>(
      '/calendar/connections'
    );
    return result.connections;
  }

  return {
    initiateOAuth,
    handleCallback,
    refreshToken,
    revokeConnection,
    getConnections,
  };
}

// ─── Singleton Instance ──────────────────────────────────────────────────────

/** Singleton OAuth service instance for the Kira app. */
export const oauthService = createOAuthService();
